import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { UserDetails } from '../../types/user-details.type';
import { UserSelectionsService } from '../../services/user-selections/user-selections.service';
import { AppSettings } from '../../PRE-LOAD/app-settings';
import { StringsService } from '../../services/strings/strings.service';
import { GdprData } from '../../types/strings.type';
import { pairwise, startWith } from 'rxjs/operators';
import { MarketingPreferencesComponent as BmwgMarketingPreferencesComponent } from 'bmwg-angular-library/lib/marketing-preferences/marketing-preferences.component';
import { SelectedMarketingOptions } from 'bmwg-angular-library/lib/marketing-preferences/marketing-preferences-types';

@Component({
    selector: 'marketing-preferences',
    templateUrl: './marketing-preferences.component.html',
    styles: [
        require('./marketing-preferences.component.scss').default.toString(),
        require(`./marketing-preferences.component.${environment.brand}.scss`).default.toString()
    ]
})
export class MarketingPreferencesComponent implements OnInit {

    gdprJson: GdprData;

    content: UserDetails;

    preferences: BmwgMarketingPreferencesComponent;

    // Form group for the details form.
    detailsForm: FormGroup;

    marketingPreferences: SelectedMarketingOptions;

    brand = environment.brand.toUpperCase();

    gdprBrand = this.appSettings.apiBrand;

    policyVersion = this.appSettings.policyVersion;

    benefitsArray = [
        {label: `Be the first to know about the latest ${this.brand} products`},
        {label: `Receive exclusive content on ${this.brand} events`},
        {label: 'Receive personalised communications'},
        {label: 'Access to competitions'}
    ];

    preferencesIntro = `I would like to receive relevant information and offers. In order to receive personalised communications, my preferences and interactions with BMW Group communications, products and services will be analysed. Selected BMW Group companies and selected authorised sales and service partners may contact me for marketing purposes, via the following channels:`;

        toolTips = {
        marketingPreferences:
            `<p>Your privacy is important to us. We act in your interests and we want to be transparent about the purposes and scope for which this marketing consent is used. We would like to explain how we use personal data in particular, to target our marketing activities and make it as relevant and interesting as possible for you. </p>

            <h4>Who will receive your data and contact you with promotional communication?</h4>
            <p>If you consent to our marketing communications, your personal data will be processed for promotion and marketing purposes by BMW (UK) Limited (BMW UK) and BMW Financial Services (GB) Limited (BMW FS), both at Summit One, Summit Avenue, Farnborough, Hampshire, GU14 0FB. You further agree that for the purposes mentioned below, BMW UK and BMW FS may share selected personal data with BMW AG, Petuelring 130, 80788 Munich, Germany which may process your data and contact you via your selected channels for the purposes mentioned below. In addition, selected personal data will also be shared with your sales and service partners (i.e. your stated preferred partner for example as set in your online account; the partner from which you purchased your vehicle; the partner you visited for service or enquiry purposes; or in cases where you have none of these, a partner that is located close to your supplied address), which may process your data and contact you via your selected channels for the purposes mentioned below. Updates may be shared between the stated companies. </p>

            <h4>What kind of promotional communication can you expect? And through which channels?</h4>
            <p>If you consent to our marketing communications, your personal data will be stored and processed to provide you with selected personalised marketing communications and information about BMW Group (including BMW Group branded) products and services. Such communications include latest news on BMW Group (including BMW Group branded) products and services; selected offers (including financial) or exclusive content on BMW Group events. </p>

            <h4>What data is transferred?</h4>
            <p>If you consent to our promotional communications, BMW UK and BMW FS may share your personal data between them (e.g. contact information, identification data, your vehicles, vehicles of interest, your consent to promotional communication, supplementary information/preferences, promotional campaigns, participation in customer / prospect programmes) and with certain BMW Group companies (BMW AG, Petuelring 130, 80788 Munich, Germany) as well as your sales and service partners (i.e. your stated preferred partner for example as set in your online account; the partner from which you purchased your vehicle; or the partner you visited for service or enquiry purposes). Additional data (e.g. customer history, vehicle usage data, data from BMW Group apps or websites, recommended action based on statistical models) can be shared with your sales partners, from whom you have purchased a vehicle. In case you do not have an existing client relationship to a sales partner, your data (e.g. contact information, identification data, your vehicle, vehicle of interest, your consent to promotional communication, supplementary information/preferences, promotional campaigns, participation in customer / prospect programs) can be shared with an authorised sales or service partner located close to your supplied address. </p>

            <h4>How does BMW Group personalise communications for you? What data is used?</h4>
            <p>We want to provide you with personalised promotional communications which are relevant to you. If you consent to our marketing communications, we will provide communications tailored to your interests and preferences. We will process your personal data to create an individual profile for you. All data aggregated in your personal profile will influence the way we inform you about relevant BMW Group (including BMW Group branded) products and services, customer care and market research purposes. </p>

            <p>
                Where available, the following information provided by you or generated by your use of BMW Group products or services of BMW UK, BMW FS, BMW AG, or your sales and service partners may be taken into account:

                </p><ul>
                    <li>
                        Contact details (e.g. name, address, e-mail address, telephone number)
                    </li>
                    <li>
                        Identification data (e.g. your customer number, contract number)
                    </li>
                    <li>
                        Your vehicle details (e.g. vehicle identification number, ConnectedDrive contracts, service packages, vehicle mapping in BMW Group apps, check control messages)
                    </li>
                    <li>
                        Your consent to promotional communication
                    </li>
                    <li>
                        Supplementary information/preferences (e.g. preferred service partner, interests, date of birth)
                    </li>
                    <li>
                        Product interests (e.g. model, vehicle type)
                    </li>
                    <li>
                        Promotional campaigns (e.g. e-mail, name, related products)
                    </li>
                    <li>
                        Participation in customer / prospect programmes (e.g. previously sent information, event participation)
                    </li>
                    <li>
                        Customer history (e.g. receipt of offers, test drives, vehicle purchase data, sales partner information; contacts with BMW Group customer service/mobile service, purchases from BMW Group online shops)
                    </li>
                    <li>
                        Vehicle usage data (e.g. mileage, range, battery charge)
                    </li>
                    <li>
                        Data from BMW Group, including apps or websites (e.g. usage data from the apps and online accounts)
                    </li>
                    <li>
                        Data from cooperation partners (e.g. data from the BMW Group branded Add-On Mobility app)
                    </li>
                    <li>
                        Data from BMW FS (e.g. contract data, vehicle data and configurations and equity position).
                    </li>
                </ul>
                <p>No information about your credit worthiness or comparable information will go into your customer profile.</p>
           `,
        optOut: `<ul>
        <h4>How can you withdraw or change your consent?</h4>
        <p>You can change your communication preferences or withdraw your consent at any time via your BMW or MINI online account. If you do not have an account, you can contact Customer Services on 0800 561 0666 or send an email to
        <a href="mailto:customer.information@bmw.co.uk" target="_blank" rel="noopener">customer.information@bmw.co.uk</a>. You can also request information about how your data is stored or ask us to correct, delete or restrict the use of your personal data.</p>
        <h4>Where can you find further information about the processing of your personal data and your related rights?</h4>
        <p>Further details regarding the processing of your personal data and your related rights, can be found online in the
        <a href="https://www.bmw.co.uk/en/footer/legal/privacy-policy.html" target="_blank" rel="noopener">BMW Group UK Privacy Policy</a>.</p>
        </div>`,
    };


    constructor(private userSelectionsService: UserSelectionsService, private appSettings: AppSettings, private stringsService: StringsService) {

    }

    ngOnInit() {

        if(this.userSelectionsService.get('marketingPreferences')){
            this.marketingPreferences = this.userSelectionsService.get('marketingPreferences');
        }else{
            this.marketingPreferences = {email: false, phone: false, sms: false, car: false, mail: false, app: false}
        }

        this.stringsService.get().subscribe((gdprJson: GdprData) => {
            this.gdprJson = gdprJson['GdprData'];
        });

        if (this.gdprBrand === this.gdprJson.brand)
        {
            this.policyVersion=this.gdprJson.policyVersion;
            this.benefitsArray=[
                {label: this.gdprJson.benefitsArray1},
                {label: this.gdprJson.benefitsArray2},
                {label: this.gdprJson.benefitsArray3},
                {label: this.gdprJson.benefitsArray4}
            ];
            this.preferencesIntro=this.gdprJson.preferencesIntro;
            this.toolTips=this.gdprJson.toolTips;
        }

    }

    checkboxesChanged($event) {
        this.marketingPreferences = $event;
        this.userSelectionsService.set('marketingPreferences', $event);
    }
}
